import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LOGIN } from '../constants/api';
import { ADMIN_CREDENTIALS, MESSAGES, CSS_CLASSES, FILE_PATHS, DEFAULTS } from '../constants/app';
import { validation, apiUtils, errorUtils } from '../utils';
import type { LoginCredentials, LoginResponse } from '../types';

interface LoginProps {
  onLogin: (email: string, isAdmin: boolean, hospitalName?: string, userId?: string) => void;
}

export default function Login({ onLogin }: LoginProps) {
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState<LoginCredentials>({ email: '', password: '' });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const updateCredentials = (field: keyof LoginCredentials, value: string) => {
    setCredentials(prev => ({ ...prev, [field]: value }));
    if (error) setError(''); // Clear error when user starts typing
  };

  const validateCredentials = (): string | null => {
    if (!validation.isRequired(credentials.email)) {
      return MESSAGES.VALIDATION.REQUIRED_FIELD;
    }
    if (!validation.isValidEmail(credentials.email)) {
      return MESSAGES.VALIDATION.INVALID_EMAIL;
    }
    if (!validation.isRequired(credentials.password)) {
      return MESSAGES.VALIDATION.REQUIRED_FIELD;
    }
    return null;
  };

  const handleAdminLogin = (): boolean => {
    if (credentials.email === ADMIN_CREDENTIALS.EMAIL && credentials.password === ADMIN_CREDENTIALS.PASSWORD) {
      onLogin(
        ADMIN_CREDENTIALS.EMAIL, 
        true, 
        ADMIN_CREDENTIALS.NAME, 
        ADMIN_CREDENTIALS.USER_ID
      );
      return true;
    }
    return false;
  };

  const handleHospitalLogin = async (): Promise<void> => {
    try {
      const response = await fetch(LOGIN, apiUtils.createRequestOptions('POST', credentials));
      const result: LoginResponse = await apiUtils.handleResponse(response);
      
      const hospitalName = result.data.full_name || DEFAULTS.HOSPITAL_NAME;
      const userId = result.data._id;
      onLogin(credentials.email, false, hospitalName, userId);
    } catch (error) {
      throw new Error(errorUtils.getErrorMessage(error));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    // Validate credentials
    const validationError = validateCredentials();
    if (validationError) {
      setError(validationError);
      return;
    }
    
    setIsLoading(true);

    try {
      // Try admin login first
      if (handleAdminLogin()) {
        return;
      }

      // Try hospital login
      await handleHospitalLogin();
      
    } catch (error) {
      errorUtils.logError(error, 'Login');
      const errorMessage = errorUtils.isNetworkError(error) 
        ? MESSAGES.ERRORS.LOGIN_FAILED 
        : errorUtils.getErrorMessage(error);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-3xl shadow-lg p-8">
       {/* Header */}
       <div className="text-center mb-8">
          <div className="mb-4">
            <img 
              src={FILE_PATHS.IMAGES.HELIUS_LOGO}
              alt="Helius" 
              className="w-30 h-30 mx-auto"
            />
          </div>
          <p className="text-gray-600">Welcome to Helius Clinician Platform</p>
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Email Field */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={credentials.email}
              onChange={(e) => updateCredentials('email', e.target.value)}
              placeholder="Email address"
              className={`${CSS_CLASSES.INPUTS.BASE} ${error ? CSS_CLASSES.INPUTS.ERROR : ''}`}
              disabled={isLoading}
              required
            />
          </div>

          {/* Password Field */}
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={credentials.password}
              onChange={(e) => updateCredentials('password', e.target.value)}
              placeholder="Password"
              className={`${CSS_CLASSES.INPUTS.BASE} ${error ? CSS_CLASSES.INPUTS.ERROR : ''}`}
              disabled={isLoading}
              required
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className={CSS_CLASSES.TEXT.ERROR + ' text-center'}>
              {error}
            </div>
          )}

          {/* Login Button */}
          <button
            type="submit"
            disabled={isLoading}
            className={`w-full py-3 px-4 rounded-lg font-medium ${CSS_CLASSES.BUTTONS.PRIMARY} ${
              isLoading ? CSS_CLASSES.BUTTONS.LOADING : ''
            }`}
          >
            {isLoading ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                {MESSAGES.LOADING.SIGNING_IN}
              </div>
            ) : (
              'Login'
            )}
          </button>

          {/* Register Link */}
          <div className="text-center hidden">
            <p className="text-gray-600">
              Don't have an account?{' '}
              <button
                type="button"
                onClick={() => navigate('/register')}
                className="text-blue-600 hover:text-blue-700 font-medium"
              >
                Create account
              </button>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
} 